import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { useCart } from "../../lib/useCart";
import {
  ShoppingBag, Star, Search, Package, Plus, SlidersHorizontal, X, ChevronDown, ChevronRight,
  Check, Tag, Flame,
} from "lucide-react";
import { toast } from "sonner";
import WishlistButton from "../../components/public/WishlistButton";
import { Icon } from "../../lib/icons";

function priceFmt(cents, symbol = "€") {
  return `${(cents / 100).toFixed(2)} ${symbol}`;
}

const SORT_OPTIONS = [
  { v: "featured", label: "Mise en avant" },
  { v: "price_asc", label: "Prix croissant" },
  { v: "price_desc", label: "Prix décroissant" },
  { v: "newest", label: "Nouveautés" },
];

function FilterSection({ title, defaultOpen = true, children, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-[hsl(var(--border))]/70 py-4" data-testid={testid}>
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between text-left">
        <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-[color:var(--brand-navy)]">{title}</span>
        <ChevronDown className={`w-4 h-4 text-[hsl(var(--muted-foreground))] transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

function CategoryTree({ cats, current, onPick }) {
  const roots = cats.filter(c => !c.parent_slug);
  return (
    <ul className="space-y-1" data-testid="filter-category-tree">
      <li>
        <button
          type="button"
          onClick={() => onPick("")}
          className={`w-full text-left px-2 py-1.5 rounded-lg text-sm flex items-center justify-between hover:bg-[hsl(var(--muted))]/50 transition ${!current ? "bg-[color:var(--brand-navy)] text-white font-semibold" : "text-[hsl(var(--foreground))]"}`}
          data-testid="filter-cat-all"
        >
          <span>Tous les produits</span>
          <span className={`text-[10px] ${!current ? "opacity-80" : "opacity-50"}`}>{cats.reduce((s, c) => s + (c.product_count || 0), 0)}</span>
        </button>
      </li>
      {roots.map(root => {
        const children = cats.filter(c => c.parent_slug === root.slug);
        const active = current === root.slug;
        const childActive = children.some(c => c.slug === current);
        return (
          <li key={root.id}>
            <button
              type="button"
              onClick={() => onPick(root.slug)}
              className={`w-full text-left px-2 py-1.5 rounded-lg text-sm flex items-center justify-between hover:bg-[hsl(var(--muted))]/50 transition ${active ? "bg-[color:var(--brand-navy)] text-white font-semibold" : "text-[hsl(var(--foreground))]"}`}
              data-testid={`filter-cat-${root.slug}`}
            >
              <span className="flex items-center gap-2 min-w-0">
                <Icon name={root.icon || "Tag"} className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{root.name}</span>
              </span>
              <span className={`text-[10px] shrink-0 ${active ? "opacity-80" : "opacity-50"}`}>{root.product_count}</span>
            </button>
            {(active || childActive) && children.length > 0 && (
              <ul className="ml-4 mt-1 space-y-0.5 border-l border-[hsl(var(--border))] pl-2">
                {children.map(c => {
                  const subActive = current === c.slug;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => onPick(c.slug)}
                        className={`w-full text-left px-2 py-1 rounded-md text-[13px] flex items-center justify-between hover:bg-[hsl(var(--muted))]/60 transition ${subActive ? "bg-[color:var(--brand-sky)]/20 text-[color:var(--brand-navy)] font-semibold" : "text-[hsl(var(--muted-foreground))]"}`}
                        data-testid={`filter-subcat-${c.slug}`}
                      >
                        <span className="truncate flex items-center gap-1"><ChevronRight className="w-3 h-3 opacity-50" /> {c.name}</span>
                        <span className="text-[10px] opacity-60">{c.product_count}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function PriceSlider({ min, max, value, onChange }) {
  const range = max - min;
  const step = Math.max(1, Math.round(range / 100));
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 px-2 py-1 rounded-lg border bg-[hsl(var(--muted))]/30 text-xs font-semibold text-[color:var(--brand-navy)] text-center tabular-nums">
          {(value[0] / 100).toFixed(0)} €
        </div>
        <span className="text-[hsl(var(--muted-foreground))]">—</span>
        <div className="flex-1 px-2 py-1 rounded-lg border bg-[hsl(var(--muted))]/30 text-xs font-semibold text-[color:var(--brand-navy)] text-center tabular-nums">
          {(value[1] / 100).toFixed(0)} €
        </div>
      </div>
      <div className="relative h-2 bg-[hsl(var(--muted))]/60 rounded-full">
        <div
          className="absolute inset-y-0 bg-[color:var(--brand-navy)] rounded-full"
          style={{
            left: `${((value[0] - min) / range) * 100}%`,
            right: `${100 - ((value[1] - min) / range) * 100}%`,
          }}
        />
      </div>
      <div className="relative">
        <input
          type="range" min={min} max={max} step={step} value={value[0]}
          onChange={e => onChange([Math.min(Number(e.target.value), value[1] - step), value[1]])}
          className="absolute inset-0 w-full accent-[color:var(--brand-navy)] bg-transparent appearance-none pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-moz-range-thumb]:pointer-events-auto"
          data-testid="filter-price-min"
        />
        <input
          type="range" min={min} max={max} step={step} value={value[1]}
          onChange={e => onChange([value[0], Math.max(Number(e.target.value), value[0] + step)])}
          className="relative w-full accent-[color:var(--brand-navy)] bg-transparent appearance-none [&::-webkit-slider-thumb]:pointer-events-auto"
          data-testid="filter-price-max"
        />
      </div>
    </div>
  );
}

export default function ShopPage() {
  const { settings: s } = useSettings();
  const { add: addToCart } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(null);      // { categories, price_range, attributes, total_products }
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // URL-synced filters
  const category = searchParams.get("category") || "";
  const q = searchParams.get("q") || "";
  const sort = searchParams.get("sort") || "featured";
  const onSale = searchParams.get("on_sale") === "1";
  const inStock = searchParams.get("in_stock") === "1";
  const priceMin = searchParams.get("price_min");
  const priceMax = searchParams.get("price_max");
  const attrsParam = searchParams.get("attrs") || "";

  const selectedAttrs = useMemo(() => {
    // "Couleur=Rouge,Rose|Taille=M" → {Couleur: ["Rouge","Rose"], Taille:["M"]}
    const out = {};
    if (!attrsParam) return out;
    for (const g of attrsParam.split("|")) {
      const [k, v] = g.split("=");
      if (!k || !v) continue;
      out[k] = v.split(",").filter(Boolean);
    }
    return out;
  }, [attrsParam]);

  const activeCount =
    (category ? 1 : 0) +
    (onSale ? 1 : 0) +
    (inStock ? 1 : 0) +
    (priceMin || priceMax ? 1 : 0) +
    Object.values(selectedAttrs).reduce((a, arr) => a + arr.length, 0);

  const [mobileOpen, setMobileOpen] = useState(false);

  const patchParams = (updates) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(updates)) {
      if (v === null || v === "" || v === undefined || v === false) next.delete(k);
      else next.set(k, String(v));
    }
    setSearchParams(next, { replace: true });
  };

  const resetAll = () => setSearchParams(new URLSearchParams(), { replace: true });

  const toggleAttr = (key, value) => {
    const current = { ...selectedAttrs };
    current[key] = current[key] || [];
    const i = current[key].indexOf(value);
    if (i >= 0) current[key].splice(i, 1);
    else current[key].push(value);
    if (!current[key].length) delete current[key];
    const serialized = Object.entries(current).map(([k, v]) => `${k}=${v.join(",")}`).join("|");
    patchParams({ attrs: serialized || null });
  };

  // Load facets when category changes (so counts reflect scope)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/shop/filters", { params: { category: category || undefined } });
        if (alive) setFilters(data);
      } catch { /* shop disabled */ }
    })();
    return () => { alive = false; };
  }, [category]);

  // Load products on any filter change
  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const params = {};
        if (category) params.category = category;
        if (q) params.q = q;
        if (sort && sort !== "featured") params.sort = sort;
        if (priceMin) params.price_min = priceMin;
        if (priceMax) params.price_max = priceMax;
        if (onSale) params.on_sale = true;
        if (inStock) params.in_stock = true;
        if (attrsParam) params.attrs = attrsParam;
        const { data } = await api.get("/shop/products", { params });
        if (alive) setItems(data || []);
      } catch { if (alive) setItems([]); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [category, q, sort, priceMin, priceMax, onSale, inStock, attrsParam]);

  const currency = s?.shop_currency_symbol || "€";

  if (!loading && !s?.shop_enabled) {
    return (
      <section className="pt-24 pb-20">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <Package className="w-14 h-14 mx-auto text-[color:var(--brand-sky)]/30 mb-4" />
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Boutique bientôt en ligne</h1>
          <p className="mt-3 text-[hsl(var(--muted-foreground))]">Notre boutique est en cours de préparation. Revenez vite !</p>
        </div>
      </section>
    );
  }

  const pr = filters?.price_range || { min: 0, max: 0 };
  const hasPriceRange = pr.max > pr.min;
  const currentPriceRange = [
    priceMin ? Number(priceMin) : pr.min,
    priceMax ? Number(priceMax) : pr.max,
  ];

  const SidebarContent = () => (
    <>
      {/* Reset */}
      {activeCount > 0 && (
        <div className="mb-2 flex items-center justify-between gap-2 text-xs">
          <span className="font-semibold text-[color:var(--brand-navy)]">{activeCount} filtre{activeCount > 1 ? "s" : ""} actif{activeCount > 1 ? "s" : ""}</span>
          <button type="button" onClick={resetAll} className="text-[color:var(--brand-red)] font-bold hover:underline" data-testid="filter-reset">
            Tout réinitialiser
          </button>
        </div>
      )}

      {/* Categories */}
      {filters?.categories?.length > 0 && (
        <FilterSection title="Catégories" testid="filter-section-category">
          <CategoryTree cats={filters.categories} current={category} onPick={(slug) => patchParams({ category: slug || null })} />
        </FilterSection>
      )}

      {/* Price */}
      {hasPriceRange && (
        <FilterSection title="Prix" testid="filter-section-price">
          <PriceSlider
            min={pr.min}
            max={pr.max}
            value={currentPriceRange}
            onChange={([lo, hi]) => patchParams({ price_min: lo !== pr.min ? lo : null, price_max: hi !== pr.max ? hi : null })}
          />
        </FilterSection>
      )}

      {/* Quick flags */}
      <FilterSection title="Disponibilité &amp; offres" testid="filter-section-flags">
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 cursor-pointer text-sm hover:text-[color:var(--brand-navy)]">
            <input type="checkbox" checked={inStock} onChange={(e) => patchParams({ in_stock: e.target.checked || null })} className="w-4 h-4 accent-[color:var(--brand-sky)]" data-testid="filter-instock" />
            En stock uniquement
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm hover:text-[color:var(--brand-navy)]">
            <input type="checkbox" checked={onSale} onChange={(e) => patchParams({ on_sale: e.target.checked || null })} className="w-4 h-4 accent-[color:var(--brand-red)]" data-testid="filter-onsale" />
            <Flame className="w-3.5 h-3.5 text-[color:var(--brand-red)]" /> En promotion
          </label>
        </div>
      </FilterSection>

      {/* Dynamic attributes */}
      {filters?.attributes?.map(a => (
        <FilterSection key={a.key} title={a.key} testid={`filter-section-attr-${a.key}`}>
          <ul className="space-y-1 max-h-48 overflow-y-auto pr-1">
            {a.values.map(v => {
              const checked = (selectedAttrs[a.key] || []).includes(v.value);
              return (
                <li key={v.value}>
                  <label className="flex items-center justify-between gap-2 cursor-pointer text-sm hover:text-[color:var(--brand-navy)] py-0.5">
                    <span className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAttr(a.key, v.value)}
                        className="w-4 h-4 accent-[color:var(--brand-sky)]"
                        data-testid={`filter-attr-${a.key}-${v.value}`}
                      />
                      <span>{v.value}</span>
                    </span>
                    <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{v.count}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </FilterSection>
      ))}
    </>
  );

  return (
    <>
      <title>{`Boutique${s?.company_name ? " — " + s.company_name : ""}`}</title>
      <section className="pt-14 pb-8 bg-[color:var(--brand-navy)] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[color:var(--brand-sky)] font-semibold mb-3">
            <ShoppingBag className="w-3.5 h-3.5" />Boutique
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black">Notre sélection</h1>
          <p className="mt-3 text-white/80 max-w-2xl">Découvrez notre catalogue — click &amp; collect disponible sur place.</p>
        </div>
      </section>

      {/* Search + sort bar */}
      <section className="py-4 border-b bg-white sticky top-14 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="lg:hidden inline-flex items-center gap-1.5 h-10 px-3 rounded-full border text-sm font-semibold bg-white"
            data-testid="filter-mobile-open"
          >
            <SlidersHorizontal className="w-4 h-4" /> Filtres
            {activeCount > 0 && <span className="min-w-[20px] h-[20px] rounded-full bg-[color:var(--brand-red)] text-white text-[10px] font-black flex items-center justify-center px-1">{activeCount}</span>}
          </button>
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
            <input
              type="text"
              value={q}
              onChange={e => patchParams({ q: e.target.value || null })}
              placeholder="Rechercher un produit..."
              className="flex-1 h-10 px-3 rounded-full border bg-[hsl(var(--muted))]/40 focus:outline-none focus:border-[color:var(--brand-sky)]"
              data-testid="shop-search-input"
            />
          </div>
          <div className="ml-auto flex items-center gap-2 text-xs">
            <span className="text-[hsl(var(--muted-foreground))] hidden sm:inline">Trier par :</span>
            <select
              value={sort}
              onChange={e => patchParams({ sort: e.target.value === "featured" ? null : e.target.value })}
              className="h-10 px-3 rounded-full border bg-white text-sm font-semibold cursor-pointer"
              data-testid="shop-sort"
            >
              {SORT_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-[280px_1fr] gap-8">
          {/* Sidebar (desktop) */}
          <aside className="hidden lg:block self-start sticky top-32" data-testid="shop-sidebar-filters">
            <SidebarContent />
          </aside>

          {/* Sidebar (mobile drawer) */}
          {mobileOpen && (
            <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm lg:hidden" onClick={() => setMobileOpen(false)} data-testid="filter-mobile-overlay">
              <div className="absolute inset-y-0 left-0 w-[85vw] max-w-sm bg-white p-5 overflow-y-auto animate-in slide-in-from-left duration-200" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between pb-3 border-b">
                  <h3 className="font-display font-bold text-lg text-[color:var(--brand-navy)]">Filtres</h3>
                  <button type="button" onClick={() => setMobileOpen(false)} className="text-[hsl(var(--muted-foreground))]"><X className="w-5 h-5" /></button>
                </div>
                <div className="pt-2">
                  <SidebarContent />
                </div>
                <div className="sticky bottom-0 bg-white pt-3 -mx-5 px-5 border-t">
                  <button type="button" onClick={() => setMobileOpen(false)} className="w-full h-11 rounded-full bg-[color:var(--brand-navy)] text-white font-bold text-sm" data-testid="filter-mobile-apply">
                    Voir {items.length} résultat{items.length > 1 ? "s" : ""}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Products grid */}
          <div>
            {/* Active filter chips */}
            {activeCount > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="active-filters-chips">
                {category && filters?.categories && (
                  <Chip label={filters.categories.find(c => c.slug === category)?.name || category} onClear={() => patchParams({ category: null })} />
                )}
                {priceMin && <Chip label={`≥ ${(priceMin / 100).toFixed(0)}€`} onClear={() => patchParams({ price_min: null })} />}
                {priceMax && <Chip label={`≤ ${(priceMax / 100).toFixed(0)}€`} onClear={() => patchParams({ price_max: null })} />}
                {inStock && <Chip label="En stock" onClear={() => patchParams({ in_stock: null })} />}
                {onSale && <Chip label="En promotion" onClear={() => patchParams({ on_sale: null })} />}
                {Object.entries(selectedAttrs).flatMap(([k, vs]) => vs.map(v => (
                  <Chip key={`${k}-${v}`} label={`${k} : ${v}`} onClear={() => toggleAttr(k, v)} />
                )))}
                <button type="button" onClick={resetAll} className="text-xs text-[color:var(--brand-red)] font-bold hover:underline ml-1">Tout effacer</button>
              </div>
            )}

            <div className="flex items-center justify-between mb-4 text-sm text-[hsl(var(--muted-foreground))]">
              <span>{loading ? "Chargement…" : `${items.length} produit${items.length > 1 ? "s" : ""} trouvé${items.length > 1 ? "s" : ""}`}</span>
            </div>

            {!loading && items.length === 0 ? (
              <div className="text-center py-20 border border-dashed rounded-2xl bg-[hsl(var(--muted))]/30">
                <Package className="w-12 h-12 mx-auto text-[hsl(var(--muted-foreground))]/30 mb-3" />
                <p className="text-[hsl(var(--muted-foreground))]">Aucun produit ne correspond à vos filtres.</p>
                {activeCount > 0 && (
                  <button type="button" onClick={resetAll} className="mt-3 text-sm font-bold text-[color:var(--brand-sky)] underline" data-testid="empty-reset">
                    Réinitialiser les filtres
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="shop-products-grid">
                {items.map(p => (
                  <div key={p.id} className="relative group">
                    <Link
                      to={`/shop/${p.slug}`}
                      className="block bg-white rounded-2xl border border-[hsl(var(--border))] overflow-hidden hover:border-[color:var(--brand-sky)] hover:-translate-y-1 transition-all duration-300 shadow-sm hover:shadow-lg"
                      data-testid={`shop-product-${p.slug}`}
                    >
                      <div className="aspect-square bg-[hsl(var(--muted))]/30 relative overflow-hidden">
                        {p.featured_image ? (
                          <img src={p.featured_image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Package className="w-12 h-12 text-[hsl(var(--muted-foreground))]/30" /></div>
                        )}
                        {p.is_featured && (
                          <span className="absolute top-2 left-2 bg-[color:var(--brand-red)] text-white px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold flex items-center gap-1">
                            <Star className="w-2.5 h-2.5" fill="white" />Top
                          </span>
                        )}
                        {p.compare_at_price_cents > p.price_cents && (
                          <span className="absolute top-2 right-2 bg-emerald-500 text-white px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold">
                            -{Math.round(((p.compare_at_price_cents - p.price_cents) / p.compare_at_price_cents) * 100)}%
                          </span>
                        )}
                      </div>
                      <div className="p-4">
                        <div className="font-display font-bold text-[color:var(--brand-navy)] line-clamp-1">{p.name}</div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-2 mt-1 min-h-[2.5em]">{p.short_description || " "}</div>
                        <div className="flex items-end justify-between mt-3">
                          <div>
                            {p.compare_at_price_cents > p.price_cents ? (
                              <div className="flex items-baseline gap-1.5">
                                <span className="text-lg font-bold text-[color:var(--brand-red)]">{priceFmt(p.price_cents, currency)}</span>
                                <span className="text-xs line-through text-[hsl(var(--muted-foreground))]">{priceFmt(p.compare_at_price_cents, currency)}</span>
                              </div>
                            ) : (
                              <span className="text-lg font-bold text-[color:var(--brand-navy)]">{priceFmt(p.price_cents, currency)}</span>
                            )}
                            {/* Réglementation alimentaire FR/UE — prix au kilo obligatoire */}
                            {!p.price_per_kg && p.is_food && p.unit_weight_g > 0 && p.price_cents > 0 && (
                              <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5" data-testid="card-unit-price">
                                {priceFmt(Math.round(p.price_cents / p.unit_weight_g * 1000), currency)} / kg · {p.unit_weight_g} g
                              </div>
                            )}
                          </div>
                          {p.stock_tracking && p.stock_qty === 0 && (
                            <span className="text-[10px] uppercase tracking-wider font-bold text-red-600">Rupture</span>
                          )}
                        </div>
                      </div>
                    </Link>
                    <div className="absolute top-2.5 right-2.5 z-10">
                      <WishlistButton productId={p.id} size="sm" />
                    </div>
                    {(!p.stock_tracking || p.stock_qty > 0) && (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); addToCart(p, 1); toast.success(`${p.name} ajouté`); }}
                        className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-red)] text-white flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-200 translate-y-2 group-hover:translate-y-0"
                        aria-label="Ajouter au panier"
                        data-testid={`shop-quickadd-${p.slug}`}
                      >
                        <Plus className="w-5 h-5" strokeWidth={2.5} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

function Chip({ label, onClear }) {
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[color:var(--brand-navy)]/8 text-[color:var(--brand-navy)] text-xs font-semibold border border-[color:var(--brand-navy)]/20">
      {label}
      <button type="button" onClick={onClear} className="hover:text-[color:var(--brand-red)]" aria-label="Retirer le filtre">
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}
